import Twemoji from "react-twemoji";
import { PuzzleType, PuzzleMetadata, RoundData } from "sockapi/puzzle-data";
import { ScopeType } from "sockapi/scope-types";
import { Link, PuzzleLink } from "components/Link";
import { Spinner } from "components/Loading";
import { Paper, Metal } from "components/Material";
import Tooltip from "components/Tooltip";
import {
  useIsPuzzleSolved,
  useScopeStateResolvable,
  useAccessibleRounds,
  useSortedAccessiblePuzzles,
  useTeamScopeState,
} from "stores/ScopeStateStore";
import { useTeamId } from "stores/ServerInteractionStore";

import "./WorldPage.css";

function Rock(props: { cutscene: PuzzleMetadata; showName?: true }) {
  const { cutscene, showName } = props;
  const isSolved = useIsPuzzleSolved(cutscene.puzName);

  if (isSolved === null) return <Spinner inline />;

  const link = (
    <PuzzleLink puzName={cutscene.puzName}>
      <Metal seed={cutscene.displayName} rotate={0} active={!isSolved}>
        <Twemoji>🎬</Twemoji>
        {showName && (
          <div className="world-rock-name">{cutscene.displayName}</div>
        )}
      </Metal>
    </PuzzleLink>
  );

  return showName ? (
    link
  ) : (
    <Tooltip text={cutscene.displayName}>{link}</Tooltip>
  );
}

function Island(props: { round: RoundData; cutscenes: PuzzleMetadata[] }) {
  const { round, cutscenes } = props;
  const teamId = useTeamId();
  const roundData = useScopeStateResolvable(
    ScopeType.ROUND,
    teamId === null ? null : { teamId, roundId: round.roundId }
  );
  const teamScopeState = useTeamScopeState();

  if (teamId === null || roundData === null || teamScopeState === null)
    return <Spinner inline />;

  const puzzles = Object.values(teamScopeState.puzzles).filter(
    ({ type, roundId }) =>
      type === PuzzleType.PUZZLE && roundId == round.roundId
  );
  const solved = puzzles.filter(({ solveTime }) => solveTime !== undefined);
  const finished = solved.some(({ isMeta, isBigMeta }) => isMeta || isBigMeta);

  return (
    <div className="world-island">
      <Link route="round" params={{ slug: round.slug }}>
        <Paper rotate={0} seed={round.displayName}>
          {round.displayName}
          <div className="stats">
            {solved.length} / {puzzles.length}
            {finished ? <Twemoji tag="span"> ✅</Twemoji> : null}
          </div>
        </Paper>
      </Link>
      <Link route="round" params={{ slug: round.slug }}>
        <img
          className="world-island-icon"
          src={"/static/images/bg/" + roundData.bgImage}
        />
      </Link>
      <div className="world-island-tray big">
        {cutscenes.map((cutscene) => (
          <Rock key={cutscene.puzName} cutscene={cutscene} />
        ))}
      </div>
    </div>
  );
}

function WorldPage() {
  const rounds = useAccessibleRounds();
  const puzzles = useSortedAccessiblePuzzles();

  if (rounds === null || puzzles === null) return <Spinner />;

  const alonePuzzles = puzzles.filter(
    ({ type, roundId }) =>
      type === PuzzleType.STORY && (!roundId || !rounds[roundId])
  );
  const sortedRounds = Object.values(rounds).sort((a, b) => a.order - b.order);
  const cursedRounds: RoundData[][] = [];
  for (let i = 1; sortedRounds.length > 0; ++i)
    cursedRounds.push(sortedRounds.splice(0, i));

  return (
    <div className="world-page">
      <div className="fabric" />
      <div className="fabric-over" />
      {alonePuzzles.length > 0 && (
        <div className="world-island-tray bigger">
          {alonePuzzles.map((cutscene) => (
            <Rock key={cutscene.puzName} cutscene={cutscene} showName />
          ))}
        </div>
      )}
      {cursedRounds.length > 0 && (
        <div className="world-island-tray biggest">
          {cursedRounds.map((rounds, i) => (
            <div key={i} className="world-island-tray biggest">
              {rounds.map((round) => (
                <Island
                  key={round.roundId}
                  round={round}
                  cutscenes={puzzles.filter(
                    ({ type, roundId }) =>
                      type === PuzzleType.STORY && roundId === round.roundId
                  )}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default WorldPage;
